const {
  buildCanonicalPageIdentifiers,
  buildSafePageReferrer,
} = require("./analytics")

const GA_ID_PATTERN = /^G-[A-Z0-9]{6,}$/
const CLOUDFLARE_SITE_TOKEN_PATTERN = /^[A-Za-z0-9_-]{16,128}$/
const GA_SCRIPT_PREFIX = "https://www.googletagmanager.com/gtag/js?id="
const CLOUDFLARE_BEACON_URL =
  "https://static.cloudflareinsights.com/beacon.min.js"

function resolveProviderConfig(environment = {}) {
  const ga = String(environment.GATSBY_GA_MEASUREMENT_ID || "").trim()
  const cloudflare = String(
    environment.GATSBY_CLOUDFLARE_WEB_ANALYTICS_TOKEN || ""
  ).trim()
  return {
    gaMeasurementId: GA_ID_PATTERN.test(ga) ? ga : null,
    cloudflareToken: CLOUDFLARE_SITE_TOKEN_PATTERN.test(cloudflare)
      ? cloudflare
      : null,
    loadersDisabled: environment.KALPILLI_DISABLE_ANALYTICS_LOADER === "1",
  }
}

function buildConsentState(enabled) {
  return {
    analytics_storage: enabled ? "granted" : "denied",
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
  }
}

function ensureGtag(target) {
  target.dataLayer = target.dataLayer || []
  target.gtag =
    target.gtag ||
    function gtag() {
      target.dataLayer.push(arguments)
    }
  return target.gtag
}

function readSafeDocumentReferrer(document) {
  try {
    return buildSafePageReferrer(document && document.referrer)
  } catch (_) {
    return ""
  }
}

function loadGoogleAnalytics(target, document, measurementId) {
  if (!target || !document || !GA_ID_PATTERN.test(measurementId || "")) {
    return false
  }
  try {
    target.__KALPILLI_GA4_MEASUREMENT_ID__ = measurementId
    target[`ga-disable-${measurementId}`] = false
    if (target.__KALPILLI_GA4_INITIALIZED__) return true
    if (!target.__KALPILLI_GA4_CONFIGURED__) {
      const gtag = ensureGtag(target)
      gtag("consent", "default", buildConsentState(true))
      gtag("set", "ads_data_redaction", true)
      gtag("js", new Date())
      gtag("config", measurementId, {
        send_page_view: false,
        cookie_expires: 0,
        anonymize_ip: true,
        allow_ad_personalization_signals: false,
        ...buildCanonicalPageIdentifiers(
          target.location && target.location.pathname
        ),
        page_referrer: readSafeDocumentReferrer(document),
      })
      target.__KALPILLI_GA4_CONFIGURED__ = true
    }
    const selector = `script[src^="${GA_SCRIPT_PREFIX}"]`
    if (!document.querySelector(selector)) {
      const script = document.createElement("script")
      script.async = true
      script.src = `${GA_SCRIPT_PREFIX}${encodeURIComponent(measurementId)}`
      script.dataset.kalpilliProvider = "ga4"
      document.head.appendChild(script)
    }
    target.__KALPILLI_GA4_INITIALIZED__ = true
    return true
  } catch (_) {
    delete target.__KALPILLI_GA4_INITIALIZED__
    return false
  }
}

function loadCloudflareWebAnalytics(target, document, token) {
  if (!target || !document || !CLOUDFLARE_SITE_TOKEN_PATTERN.test(token || ""))
    return false
  if (target.__KALPILLI_CLOUDFLARE_INITIALIZED__) return true
  try {
    const existing = document.querySelector(
      `script[src^="${CLOUDFLARE_BEACON_URL}"],script[data-cf-beacon]`
    )
    if (!existing) {
      const script = document.createElement("script")
      script.type = "module"
      script.defer = true
      script.src = CLOUDFLARE_BEACON_URL
      script.dataset.cfBeacon = JSON.stringify({ token, spa: false })
      document.head.appendChild(script)
    }
    target.__KALPILLI_CLOUDFLARE_INITIALIZED__ = true
    return true
  } catch (_) {
    delete target.__KALPILLI_CLOUDFLARE_INITIALIZED__
    return false
  }
}

function denyFutureMeasurement(target) {
  try {
    target.__KALPILLI_MEASUREMENT_STATE__ = "disabled"
    const configuredId =
      target.__KALPILLI_MEASUREMENT_CONFIG__ &&
      target.__KALPILLI_MEASUREMENT_CONFIG__.gaMeasurementId
    const measurementIds = [
      target.__KALPILLI_GA4_MEASUREMENT_ID__,
      configuredId,
    ].filter(candidate => GA_ID_PATTERN.test(candidate || ""))
    const uniqueMeasurementIds = [...new Set(measurementIds)]
    if (
      !GA_ID_PATTERN.test(target.__KALPILLI_GA4_MEASUREMENT_ID__ || "") &&
      uniqueMeasurementIds.length > 0
    ) {
      target.__KALPILLI_GA4_MEASUREMENT_ID__ = uniqueMeasurementIds[0]
    }
    for (const measurementId of uniqueMeasurementIds) {
      target[`ga-disable-${measurementId}`] = true
    }
    if (typeof target.gtag === "function") {
      target.gtag("consent", "update", buildConsentState(false))
    }
    return true
  } catch (_) {
    return false
  }
}

function deleteKnownGaCookies(document, hostname = "") {
  try {
    if (!document || typeof document.cookie !== "string") return false
    const names = document.cookie
      .split(";")
      .map(part => part.split("=")[0].trim())
      .filter(name => /^_ga(?:_|$)|^_gid$|^_gat/.test(name))
    const domains = new Set([
      hostname,
      hostname ? `.${hostname.replace(/^\./, "")}` : "",
      hostname === "kalpilli.com" || hostname.endsWith(".kalpilli.com")
        ? ".kalpilli.com"
        : "",
    ])
    for (const name of names) {
      document.cookie = `${name}=; Max-Age=0; path=/; SameSite=Lax`
      for (const domain of domains) {
        if (domain) {
          document.cookie = `${name}=; Max-Age=0; path=/; domain=${domain}; SameSite=Lax`
        }
      }
    }
    return true
  } catch (_) {
    return false
  }
}

module.exports = {
  buildConsentState,
  deleteKnownGaCookies,
  denyFutureMeasurement,
  loadCloudflareWebAnalytics,
  loadGoogleAnalytics,
  resolveProviderConfig,
}
